export const VISUAL_PREFERENCES_STORAGE_KEY = 'ep:visual-preferences:v1';
export const DEFAULT_VISUAL_PREFERENCES = Object.freeze({
  theme: 'system',
  scale: 'default',
});

export const UI_THEMES = Object.freeze(['light', 'dark', 'system']);
export const UI_SCALES = Object.freeze(['compact', 'default', 'confort']);
let systemThemeMedia = null;
let systemThemeListener = null;

export const normalizeVisualTheme = (value, fallback = DEFAULT_VISUAL_PREFERENCES.theme) => {
  const normalized = String(value || '').trim().toLowerCase();
  return UI_THEMES.includes(normalized) ? normalized : fallback;
};

export const normalizeVisualScale = (value, fallback = DEFAULT_VISUAL_PREFERENCES.scale) => {
  const normalized = String(value || '').trim().toLowerCase();
  return UI_SCALES.includes(normalized) ? normalized : fallback;
};

export const normalizeVisualPreferences = (value = {}) => ({
  theme: normalizeVisualTheme(value?.theme),
  scale: normalizeVisualScale(value?.scale),
});

const getStorage = (storage = null) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

export const readVisualPreferences = (storage = null) => {
  try {
    const raw = getStorage(storage)?.getItem(VISUAL_PREFERENCES_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VISUAL_PREFERENCES };
    return normalizeVisualPreferences(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_VISUAL_PREFERENCES };
  }
};

export const writeVisualPreferences = (preferences, storage = null) => {
  const normalized = normalizeVisualPreferences(preferences);
  try {
    getStorage(storage)?.setItem(VISUAL_PREFERENCES_STORAGE_KEY, JSON.stringify({
      version: 1,
      ...normalized,
    }));
  } catch {}
  return normalized;
};

export const resolveVisualTheme = (theme, matchMedia = null) => {
  const normalized = normalizeVisualTheme(theme);
  if (normalized !== 'system') return normalized;
  const media = matchMedia
    || (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null);
  return media?.matches ? 'dark' : 'light';
};

export const applyResolvedVisualTheme = (resolvedTheme, root = null, body = null) => {
  const active = resolvedTheme === 'dark' ? 'dark' : 'light';
  const documentRoot = root || (typeof document !== 'undefined' ? document.documentElement : null);
  const documentBody = body || (typeof document !== 'undefined' ? document.body : null);
  [documentRoot, documentBody].filter(Boolean).forEach((element) => {
    element.classList.remove('light', 'dark', 'system');
    element.classList.add(active);
  });
  if (documentRoot) documentRoot.style.colorScheme = active;
  return active;
};

export const applyVisualPreferences = (preferences, options = {}) => {
  const normalized = normalizeVisualPreferences(preferences);
  const root = options.root || (typeof document !== 'undefined' ? document.documentElement : null);
  const body = options.body || (typeof document !== 'undefined' ? document.body : null);
  const resolvedTheme = resolveVisualTheme(normalized.theme, options.matchMedia);

  if (root) {
    root.dataset.uiTheme = normalized.theme;
    root.dataset.uiScale = normalized.scale;
  }
  applyResolvedVisualTheme(resolvedTheme, root, body);

  if (typeof window !== 'undefined' && options.watchSystem !== false) {
    if (systemThemeMedia && systemThemeListener) {
      systemThemeMedia.removeEventListener?.('change', systemThemeListener);
      systemThemeMedia.removeListener?.(systemThemeListener);
      systemThemeMedia = null;
      systemThemeListener = null;
    }
    if (normalized.theme === 'system' && typeof window.matchMedia === 'function') {
      systemThemeMedia = window.matchMedia('(prefers-color-scheme: dark)');
      systemThemeListener = (event) => applyResolvedVisualTheme(
        event.matches ? 'dark' : 'light',
        root,
        body
      );
      if (systemThemeMedia.addEventListener) {
        systemThemeMedia.addEventListener('change', systemThemeListener);
      } else {
        systemThemeMedia.addListener?.(systemThemeListener);
      }
    }
  }
  if (options.persist === true) {
    writeVisualPreferences(normalized, options.storage);
  }
  return { ...normalized, resolvedTheme };
};

export const applyStoredVisualPreferences = (options = {}) => (
  applyVisualPreferences(readVisualPreferences(options.storage), options)
);
